import { useCallback, useRef, useState } from 'react';
import ForceGraph2D, { type ForceGraphMethods } from 'react-force-graph-2d';
import type { GraphData, GraphNode, GraphEdge, NodeType } from '../../types/knowledge';
import { NODE_COLORS } from '../../types/knowledge';

interface Props {
  data: GraphData;
  onNodeClick?: (node: GraphNode) => void;
  height?: number;
  filterTypes?: NodeType[];
}

export default function GraphVisualization({ data, onNodeClick, height = 500, filterTypes }: Props) {
  const fgRef = useRef<ForceGraphMethods>();
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Transform data for react-force-graph-2d
  const filtered = filterTypes && filterTypes.length > 0
    ? {
        nodes: data.nodes.filter(n => filterTypes.includes(n.type as NodeType)),
        edges: data.edges,
      }
    : data;

  const graphData = {
    nodes: filtered.nodes.map(n => ({ ...n, id: n.id })),
    links: filtered.edges.map(e => ({
      source: e.from_node,
      target: e.to_node,
      label: e.relationship,
    })),
  };

  const handleNodeClick = useCallback((node: Record<string, unknown>) => {
    onNodeClick?.(node as unknown as GraphNode);
    // Zoom to node
    fgRef.current?.centerAt(node.x as number, node.y as number, 600);
    fgRef.current?.zoom(3, 600);
  }, [onNodeClick]);

  const paintNode = useCallback(
    (node: Record<string, unknown>, ctx: CanvasRenderingContext2D) => {
      const id = node.id as string;
      const type = (node.type as NodeType) || 'Entity';
      const label = (node.label as string) || id;
      const color = NODE_COLORS[type] || '#64748B';
      const isHovered = hoveredNode === id;
      const x = node.x as number;
      const y = node.y as number;
      const r = type === 'Document' ? 8 : 6;

      // Draw circle
      ctx.beginPath();
      ctx.arc(x, y, isHovered ? r + 2 : r, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      if (isHovered) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Draw label
      if (isHovered || type === 'Document') {
        ctx.font = '4px Inter, sans-serif';
        ctx.fillStyle = '#1E293B';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const maxLen = 20;
        const displayLabel = label.length > maxLen ? label.slice(0, maxLen) + '…' : label;
        ctx.fillText(displayLabel, x, y + r + 5);
      }
    },
    [hoveredNode],
  );

  return (
    <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        height={height}
        nodeCanvasObject={paintNode}
        nodePointerAreaPaint={(node, color, ctx) => {
          const n = node as Record<string, unknown>;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(n.x as number, n.y as number, 10, 0, 2 * Math.PI);
          ctx.fill();
        }}
        onNodeClick={handleNodeClick}
        onNodeHover={(node) => setHoveredNode(node ? (node as Record<string, unknown>).id as string : null)}
        linkLabel={(link: Record<string, unknown>) => link.label as string}
        linkDirectionalArrowLength={4}
        linkDirectionalArrowRelPos={1}
        linkColor={(link: Record<string, unknown>) => {
          const label = link.label as string;
          if (label === 'CITES') return '#DC2626';        // red — document→document citation
          if (label === 'REFERENCES') return '#7C3AED';   // purple — document→regulation
          if (label === 'SUPERSEDES') return '#EA580C';   // orange
          return '#CBD5E1';                                // default grey
        }}
        linkWidth={(link: Record<string, unknown>) => {
          const label = link.label as string;
          if (label === 'CITES' || label === 'REFERENCES') return 2.5;
          return 1.5;
        }}
        backgroundColor="#F8FAFC"
        cooldownTicks={100}
      />

      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm border border-gray-100">
        <p className="text-xs font-semibold text-gray-500 mb-1.5">Legenda</p>
        <div className="space-y-1">
          {Object.entries(NODE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-xs text-gray-600">{type}</span>
            </div>
          ))}
          <div className="border-t border-gray-100 pt-1 mt-1 space-y-1">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5" style={{ backgroundColor: '#DC2626' }} />
              <span className="text-xs text-gray-600">CITES (dok → dok)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5" style={{ backgroundColor: '#7C3AED' }} />
              <span className="text-xs text-gray-600">REFERENCES</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
